"use client";

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/navbar";
import { Footer } from "../components/footer";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
// import { BeneficiarioModal } from "../components/beneficiario-modal";
import { DeleteConfirmationDialog } from "../components/delete-confirmation-dialog";
import { SmartDeleteDialog } from "../components/smart-delete-dialog";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  AlertCircle,
  Plus,
  Trash2,
  CheckCircle,
  UserPlus,
  Percent,
  Edit,
  Undo2,
  History,
  FileCheck,
  Copy,
  PlusCircle,
} from "lucide-react";
import { apiClient, type Poliza, type ApiBeneficiario } from "../lib/api";
import {
  getAllBeneficiarios,
  deleteBeneficiario,
  markApiBeneficiarioForDeletion,
  unmarkApiBeneficiarioFromDeletion,
  isApiBeneficiarioMarkedForDeletion,
  getModifiedApiBeneficiario,
  hasAnyChanges,
  getTotalPercentageByPoliza,
  getActionsForPoliza,
  replicateActionsToPolizas,
  removeReplicatedActions,
  type BeneficiarioLocal,
  markApiBeneficiarioAsModified,
  updateBeneficiario,
} from "../lib/beneficiarios-storage";
import {
  roundPercentage,
  isComplete,
  normalizePercentages,
  distributePercentageEqually,
} from "../lib/percentage-utils";
import { formatRutFromParts } from "../lib/rut-validator";

import { useAuthStore } from "../lib/stores/auth-store";
import { usePolizasCacheStore } from "../lib/stores/polizas-cache-store";
import { useBeneficiariosStore } from "../../lib/stores/beneficiarios-store";
import { BeneficiarioModalForm } from "@/components/modalForm-Beneficiario";
import { useConsentStore } from "@/lib/stores/consentimiento-store";
import { getAllBeneficiariosStorage } from "../lib/beneficiarios-storage";
import { ErrorDialog } from "@/components/ErrorDialog";
import PolizasForm from "@/components/polizasForm";

export default function PolizasPage() {
  const baseUrl = import.meta.env.VITE_BASE_URL;
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const {
    polizas: cachedPolizas,
    setPolizas: setCachedPolizas,
    shouldRefetch,
  } = usePolizasCacheStore();
  const { asegurado } = useAuthStore();
  const [polizas, setPolizas] = useState<Poliza[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [shouldCheckForPolizas, setShouldCheckForPolizas] = useState(true);
  const [seePlan, setseePlan] = useState(false);
  const [seeRutEmpresa, setseeRutEmpresa] = useState(false);
  const [selectedPoliza, setSelectedPoliza] = useState<number | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [showPolizaModal, setShowPolizaModal] = useState(false);
  const [errorList, setErrorList] = useState<
    Array<{
      rut: string;
      nombre: string;
      errors: string[];
      beneficiarioId: number | string;
    }>
  >([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [beneficiariosByPoliza, setBeneficiariosByPoliza] = useState<
    Record<number, BeneficiarioLocal[]>
  >({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [beneficiaryToDelete, setBeneficiaryToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [apiDeleteDialogOpen, setApiDeleteDialogOpen] = useState(false);
  const hasUserSeenModal = usePolizasCacheStore(
    (state) => state.hasUserSeenModal
  );

  const [apiBeneficiaryToDelete, setApiBeneficiaryToDelete] = useState<{
    id: number;
    rut: number;
    numeroPoliza: number;
    name: string;
  } | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [beneficiaryToEdit, setBeneficiaryToEdit] =
    useState<ApiBeneficiario | null>(null);
  const [deletedApiBeneficiaries, setDeletedApiBeneficiaries] = useState<
    Set<number>
  >(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [smartDeleteDialogOpen, setSmartDeleteDialogOpen] = useState(false);
  const [smartDeleteData, setSmartDeleteData] = useState<{
    beneficiary: {
      id: number | string;
      rut: number;
      numeroPoliza: number;
      name: string;
      percentage: number;
      type: "api" | "added";
    };
    remainingBeneficiaries: Array<{
      id: number;
      name: string;
      percentage: number;
    }>;
    totalAfterDeletion: number;
  } | null>(null);

  const [replicationEnabled, setReplicationEnabled] = useState<
    Record<number, boolean>
  >({});
  const [firstCompletedPoliza, setFirstCompletedPoliza] = useState<
    number | null
  >(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!asegurado) {
      navigate(`/`);
      return;
    }
    loadPolizas;

    const cachedPolizasFromStore = usePolizasCacheStore.getState().polizas;
    //
    if (cachedPolizasFromStore.length > 0) {
      // Si hay pólizas en el store, usarlas directamente
      setPolizas(cachedPolizasFromStore);
      setLoading(false);
      setShouldCheckForPolizas(false);
      setseePlan(true);
      setseeRutEmpresa(false);
    } else if (shouldRefetch()) {
      loadPolizas(asegurado.rut);
    } else {
      setPolizas(cachedPolizas);
      setLoading(false);
    }

    loadBeneficiarios();
  }, [asegurado, shouldRefetch, cachedPolizas]);

  useEffect(() => {
    if (!loading && shouldCheckForPolizas && asegurado) {
      const store = usePolizasCacheStore.getState();

      // Verificar condiciones para mostrar el modal:
      // 1. No hay pólizas en el store
      // 2. No hay pólizas del API
      // 3. El usuario no ha visto el modal antes (o queremos forzarlo)

      if (polizas.length === 0 && store.polizas.length === 0) {
        // Pequeño delay para asegurar que todo se cargó
        const timer = setTimeout(() => {
          setShowPolizaModal(true);
          // Marcar que ya revisamos
          setShouldCheckForPolizas(false);

          setseePlan(false);
          setseeRutEmpresa(true);
        }, 500);

        return () => clearTimeout(timer);
      }
    }
  }, [loading, polizas.length, shouldCheckForPolizas, asegurado]);

  useEffect(() => {
    if (polizas.length > 0) {
      const deleted = new Set<number>();
      polizas.forEach((poliza) => {
        poliza.beneficiarios?.forEach((b) => {
          if (isApiBeneficiarioMarkedForDeletion(b.id)) {
            deleted.add(b.id);
          }
        });
      });
      setDeletedApiBeneficiaries(deleted);
    }
  }, [polizas]);

  useEffect(() => {
    if (mounted) {
      setHasChanges(hasAnyChanges());
    }
  }, [mounted, beneficiariosByPoliza, deletedApiBeneficiaries]);

  useEffect(() => {
    if (!mounted || polizas.length <= 1) return;

    for (const poliza of polizas) {
      const apiBeneficiarios = poliza.beneficiarios || [];
      const totalPercentage = getTotalPercentageByPoliza(
        poliza.numeroPoliza,
        apiBeneficiarios
      );
      const actions = getActionsForPoliza(poliza.numeroPoliza);
      const hasActions =
        actions.added.length > 0 ||
        actions.modified.length > 0 ||
        actions.deleted.length > 0;

      if (isComplete(totalPercentage) && hasActions) {
        if (firstCompletedPoliza === null) {
          setFirstCompletedPoliza(poliza.numeroPoliza);
        }
        break;
      }
    }
  }, [
    mounted,
    polizas,
    beneficiariosByPoliza,
    deletedApiBeneficiaries,
    firstCompletedPoliza,
  ]);

  const getRutContratanteFormateado = (
    numeroPoliza: number
  ): string | undefined => {
    const poliza = polizas.find((p) => p.numeroPoliza === numeroPoliza);

    if (!poliza?.rutFormatedContratante) {
      return undefined;
    }

    const rutStr = poliza.rutFormatedContratante.toString();

    return `${rutStr}`;
  };

  const loadPolizas = async (rut: number) => {
    setLoading(true);
    setError("");

    const response = await apiClient.getPolizas(rut);

    setLoading(false);

    if (response.success && response.data) {
      if (response.data.length > 0) {
        const polizasActuales = polizas;
        const polizasAPI = response.data;

        const nuevasPolizas = polizasAPI.filter(
          (apiPoliza) =>
            !polizasActuales.some(
              (actualPoliza) => actualPoliza.id === apiPoliza.id
            )
        );

        const todasPolizas = [...polizasActuales, ...nuevasPolizas];

        setCachedPolizas(todasPolizas);
        setPolizas(todasPolizas);
        setShouldCheckForPolizas(false);
        setseePlan(true);
        setseeRutEmpresa(false);
      } else {
        setPolizas([]);
        setCachedPolizas([]);
        setError("No tiene pólizas asociadas");
        setseePlan(false);
        setseeRutEmpresa(true);

        if (!hasUserSeenModal) {
          setShowPolizaModal(true);
        }
      }
    } else {
      setseePlan(false);
      setseeRutEmpresa(true);
      setError(response.message || "Error al cargar pólizas");
    }
  };

  const toggleItem = (rut: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rut)) {
        newSet.delete(rut);
      } else {
        newSet.add(rut);
      }
      return newSet;
    });
  };

  const loadBeneficiarios = () => {
    const allBeneficiarios = getAllBeneficiarios();
    const grouped: Record<number, BeneficiarioLocal[]> = {};

    for (const beneficiario of allBeneficiarios) {
      if (!grouped[beneficiario.numeroPoliza]) {
        grouped[beneficiario.numeroPoliza] = [];
      }
      grouped[beneficiario.numeroPoliza]!.push(beneficiario);
    }

    setBeneficiariosByPoliza(grouped);
  };

  const handleReplicationToggle = useCallback(
    (numeroPoliza: number, enabled: boolean) => {
      setReplicationEnabled((prev) => {
        const nuevoEstado = { ...prev, [numeroPoliza]: enabled };

        return nuevoEstado;
      });

      if (enabled) {
        const targetPolizas = polizas
          .filter((p) => p.numeroPoliza !== numeroPoliza)
          .filter((p) => {
            // Calcular porcentaje total actual de la póliza destino
            const currentPercentage = getTotalPercentageByPoliza(
              p.numeroPoliza,
              p.beneficiarios || []
            );

            // Solo incluir pólizas con menos del 100%
            const isValid = currentPercentage < 100;

            return isValid;
          })
          .map((p) => p.numeroPoliza);
        console.log("Target polizas for replication:", targetPolizas);

        const apiBeneficiariosByPoliza: Record<
          number,
          Array<{ id: number; rut: number; porcentaje: number }>
        > = {};
        for (const poliza of polizas) {
          apiBeneficiariosByPoliza[poliza.numeroPoliza] =
            poliza.beneficiarios?.map((b) => ({
              id: b.id,
              rut: b.rut,
              porcentaje: b.porcentaje,
            })) || [];
        }

        replicateActionsToPolizas(
          numeroPoliza,
          targetPolizas,
          apiBeneficiariosByPoliza
        );
      } else {
        removeReplicatedActions(numeroPoliza);
      }

      loadBeneficiarios();
      setHasChanges(hasAnyChanges());

      setTimeout(() => {
        if (asegurado) {
          loadPolizas(asegurado.rut);
        }
      }, 100);
    },
    [
      replicationEnabled,
      polizas,
      replicateActionsToPolizas,
      removeReplicatedActions,
      setReplicationEnabled,
      asegurado,
    ]
  );

  const handleAddBeneficiary = (numeroPoliza: number) => {
    setSelectedPoliza(numeroPoliza);
    setEditMode(false);
    setBeneficiaryToEdit(null);
    setModalOpen(true);
  };

  const handleEditApiBeneficiary = (
    numeroPoliza: number,
    beneficiario: ApiBeneficiario
  ) => {
    const isAddedBeneficiary =
      beneficiario.id.toString().includes("replicated") ||
      beneficiario.id.toString().includes("temp") ||
      beneficiario.id.toString().includes("added");

    if (isAddedBeneficiary) {
      // Para beneficiarios agregados, usar los datos directamente
      setSelectedPoliza(numeroPoliza);
      setEditMode(true);
      setBeneficiaryToEdit(beneficiario);
      setModalOpen(true);
    } else {
      // Para beneficiarios del API, buscar en modificados
      const modified = getModifiedApiBeneficiario(beneficiario.id);
      const dataToEdit = modified || beneficiario;

      setSelectedPoliza(numeroPoliza);
      setEditMode(true);
      setBeneficiaryToEdit(dataToEdit as ApiBeneficiario);
      setModalOpen(true);
    }
  };

  const handleBeneficiaryAdded = () => {
    loadBeneficiarios();
    setHasChanges(hasAnyChanges());
  };

  interface ContactError {
    beneficiarioId: number;
    nombre: string;
    camposFaltantes: string[];
    numeroPoliza?: number; // ← ÚTIL para identificar la póliza
    tipoBeneficiario?: string;
  }

  interface ValidationResult {
    tieneErrores: boolean;
    errores: ContactError[];
  }

  function checkMissingContactDataMultiple(
    beneficiarios: ApiBeneficiario[] | undefined
  ): ValidationResult {
    if (!beneficiarios || beneficiarios.length === 0) {
      return { tieneErrores: false, errores: [] };
    }

    const errores: ContactError[] = [];

    beneficiarios.forEach((beneficiario) => {
      const errorBeneficiario = checkMissingContactData(beneficiario);
      if (errorBeneficiario.camposFaltantes.length > 0) {
        errores.push(errorBeneficiario);
      }
    });

    return {
      tieneErrores: errores.length > 0,
      errores,
    };
  }

  function checkMissingContactData(
    beneficiario: ApiBeneficiario | undefined
  ): ContactError {
    if (!beneficiario) {
      return {
        beneficiarioId: 0,
        nombre: "Beneficiario desconocido",
        camposFaltantes: ["Todos los campos"],
      };
    }

    const requiredContactFields = [
      { key: "email", label: "Email" },
      { key: "celular", label: "Celular" },
      { key: "direccion", label: "Dirección" },
      { key: "region", label: "Región" },
      { key: "ciudad", label: "Ciudad" },
      { key: "comuna", label: "Comuna" },
      { key: "casaDepto", label: "Casa/Depto" },
    ];

    const camposFaltantes: string[] = [];

    requiredContactFields.forEach((field) => {
      const value = beneficiario[field.key as keyof typeof beneficiario];
      if (!value || value === "" || value === "0") {
        camposFaltantes.push(field.label);
      }
    });

    return {
      beneficiarioId: beneficiario.id,
      nombre: `${beneficiario.nombre} ${beneficiario.apellidoPaterno}`,
      camposFaltantes,
    };
  }

  const handleDeleteBeneficiary = (id: string, name: string) => {
    const beneficiario = Object.values(beneficiariosByPoliza)
      .flat()
      .find((b) => b.id === id);

    if (!beneficiario) return;

    const numeroPoliza = beneficiario.numeroPoliza;
    const poliza = polizas.find((p) => p.numeroPoliza === numeroPoliza);
    if (!poliza) return;

    const apiBeneficiarios = poliza.beneficiarios || [];
    const addedBeneficiarios = beneficiariosByPoliza[numeroPoliza] || [];

    const remainingApiBeneficiarios = apiBeneficiarios.filter(
      (b) => !deletedApiBeneficiaries.has(b.id)
    );
    const remainingAddedBeneficiarios = addedBeneficiarios.filter(
      (b) => b.id !== id
    );

    const remainingWithModifications = remainingApiBeneficiarios.map((b) => {
      const modified = getModifiedApiBeneficiario(b.id);
      return modified || b;
    });

    const allRemainingBeneficiaries = [
      ...remainingWithModifications.map((b) => ({
        id: b.id,
        name: `${b.nombre} ${b.apellidoPaterno}`,
        percentage: b.porcentaje,
      })),
      ...remainingAddedBeneficiarios.map((b) => ({
        id: Number.parseInt(b.id),
        name: `${b.nombre} ${b.apellidoPaterno}`,
        percentage: b.porcentaje,
      })),
    ];

    const totalAfterDeletion = allRemainingBeneficiaries.reduce(
      (sum, b) => sum + b.percentage,
      0
    );

    if (allRemainingBeneficiaries.length > 0) {
      setSmartDeleteData({
        beneficiary: {
          id: id,
          rut: beneficiario.rut,
          numeroPoliza: numeroPoliza,
          name: name,
          percentage: beneficiario.porcentaje,
          type: "added",
        },
        remainingBeneficiaries: allRemainingBeneficiaries,
        totalAfterDeletion: totalAfterDeletion,
      });
      setSmartDeleteDialogOpen(true);
    } else {
      performDeleteBeneficiary(id, numeroPoliza);
    }
  };

  const performDeleteBeneficiary = (id: string, numeroPoliza: number) => {
    const beneficiario = Object.values(beneficiariosByPoliza)
      .flat()
      .find((b) => b.id === id);

    if (!beneficiario) return;

    const poliza = polizas.find((p) => p.numeroPoliza === numeroPoliza);
    if (!poliza) return;

    const apiBeneficiarios = poliza.beneficiarios || [];
    const addedBeneficiarios = beneficiariosByPoliza[numeroPoliza] || [];

    const remainingApiBeneficiarios = apiBeneficiarios.filter(
      (b) => !deletedApiBeneficiaries.has(b.id)
    );
    const remainingAddedBeneficiarios = addedBeneficiarios.filter(
      (b) => b.id !== id
    );

    const remainingWithModifications = remainingApiBeneficiarios.map((b) => {
      const modified = getModifiedApiBeneficiario(b.id);
      return modified || b;
    });

    const allRemainingBeneficiaries = [
      ...remainingWithModifications.map((b) => ({
        id: b.id,
        type: "api" as const,
        percentage: b.porcentaje,
      })),
      ...remainingAddedBeneficiarios.map((b) => ({
        id: b.id,
        type: "added" as const,
        percentage: b.porcentaje,
      })),
    ];

    deleteBeneficiario(id);

    if (allRemainingBeneficiaries.length > 0) {
      const currentTotal = allRemainingBeneficiaries.reduce(
        (sum, b) => sum + b.percentage,
        0
      );
      const deletedPercentage = beneficiario.porcentaje;
      const totalAfterRedistribution = currentTotal + deletedPercentage;

      if (totalAfterRedistribution > 100) {
        const existingPercentages = allRemainingBeneficiaries.map(
          (b) => b.percentage
        );
        const normalizedPercentages = normalizePercentages(existingPercentages);

        allRemainingBeneficiaries.forEach((remaining, index) => {
          const normalizedPercentage = normalizedPercentages[index];

          if (remaining.type === "api") {
            const apiBeneficiario = poliza.beneficiarios?.find(
              (b) => b.id === remaining.id
            );
            if (apiBeneficiario) {
              markApiBeneficiarioAsModified({
                id: apiBeneficiario.id,
                numeroPoliza: numeroPoliza,
                tipoBeneficiario: apiBeneficiario.tipoBeneficiario || 4,
                rut: apiBeneficiario.rut,
                dv: apiBeneficiario.dv,
                nombre: apiBeneficiario.nombre,
                apellidoPaterno: apiBeneficiario.apellidoPaterno,
                apellidoMaterno: apiBeneficiario.apellidoMaterno,
                fechaNacimiento: apiBeneficiario.fechaNacimiento,
                parentesco:
                  typeof apiBeneficiario.parentesco === "string"
                    ? 0
                    : apiBeneficiario.parentesco,
                email: apiBeneficiario.email || "",
                region: apiBeneficiario.region || 0,
                ciudad: apiBeneficiario.ciudad || 0,
                comuna: apiBeneficiario.comuna || 0,
                direccion: apiBeneficiario.direccion || "",
                casaDepto: apiBeneficiario.casaDepto || "",
                celular: apiBeneficiario.celular || "",
                porcentaje: normalizedPercentage ?? 0,
                sexo: apiBeneficiario.sexo || 0,
              });
            }
          } else {
            const addedBeneficiario = remainingAddedBeneficiarios.find(
              (b) => b.id === remaining.id
            );
            if (addedBeneficiario) {
              updateBeneficiario(addedBeneficiario.id, {
                ...addedBeneficiario,
                porcentaje: normalizedPercentage,
              });
            }
          }
        });
      } else {
        const existingPercentages = allRemainingBeneficiaries.map(
          (b) => b.percentage
        );
        const redistributedPercentages = distributePercentageEqually(
          deletedPercentage,
          allRemainingBeneficiaries.length,
          existingPercentages
        );

        allRemainingBeneficiaries.forEach((remaining, index) => {
          const newPercentage = redistributedPercentages[index];

          if (remaining.type === "api") {
            const apiBeneficiario = poliza.beneficiarios?.find(
              (b) => b.id === remaining.id
            );
            if (apiBeneficiario) {
              markApiBeneficiarioAsModified({
                id: apiBeneficiario.id,
                numeroPoliza: numeroPoliza,
                tipoBeneficiario: apiBeneficiario.tipoBeneficiario || 4,
                rut: apiBeneficiario.rut,
                dv: apiBeneficiario.dv,
                nombre: apiBeneficiario.nombre,
                apellidoPaterno: apiBeneficiario.apellidoPaterno,
                apellidoMaterno: apiBeneficiario.apellidoMaterno,
                fechaNacimiento: apiBeneficiario.fechaNacimiento,
                parentesco:
                  typeof apiBeneficiario.parentesco === "string"
                    ? 0
                    : apiBeneficiario.parentesco,
                email: apiBeneficiario.email || "",
                region: apiBeneficiario.region || 0,
                ciudad: apiBeneficiario.ciudad || 0,
                comuna: apiBeneficiario.comuna || 0,
                direccion: apiBeneficiario.direccion || "",
                casaDepto: apiBeneficiario.casaDepto || "",
                celular: apiBeneficiario.celular || "",
                porcentaje: newPercentage ?? 0,
                sexo: apiBeneficiario.sexo || 0,
              });
            }
          } else {
            const addedBeneficiario = remainingAddedBeneficiarios.find(
              (b) => b.id === remaining.id
            );
            if (addedBeneficiario) {
              updateBeneficiario(addedBeneficiario.id, {
                ...addedBeneficiario,
                porcentaje: newPercentage,
              });
            }
          }
        });
      }
    }

    loadBeneficiarios();
    setHasChanges(hasAnyChanges());

    setTimeout(() => {
      if (asegurado) {
        loadPolizas(asegurado.rut);
      }
    }, 100);
  };

  const handleDeleteApiBeneficiary = (
    id: number,
    rut: number,
    numeroPoliza: number,
    name: string
  ) => {
    const poliza = polizas.find((p) => p.numeroPoliza === numeroPoliza);
    if (!poliza) return;

    const beneficiario = poliza.beneficiarios?.find((b) => b.id === id);
    if (!beneficiario) return;

    const modified = getModifiedApiBeneficiario(beneficiario.id);
    const currentBeneficiario = modified || beneficiario;
    const currentPercentage = currentBeneficiario.porcentaje;

    const apiBeneficiarios = poliza.beneficiarios || [];
    const addedBeneficiarios = beneficiariosByPoliza[numeroPoliza] || [];

    const remainingApiBeneficiarios = apiBeneficiarios.filter(
      (b) => b.id !== id && !deletedApiBeneficiaries.has(b.id)
    );

    const remainingWithModifications = remainingApiBeneficiarios.map((b) => {
      const modified = getModifiedApiBeneficiario(b.id);
      return modified || b;
    });

    const beneficiariesWithRealModifications = remainingWithModifications.map(
      (b) => {
        const original = apiBeneficiarios.find(
          (originalB) => originalB.id === b.id
        );
        const wasManuallyModified =
          original &&
          (b.porcentaje !== original.porcentaje ||
            b.nombre !== original.nombre ||
            b.apellidoPaterno !== original.apellidoPaterno);

        return {
          ...b,
          wasManuallyModified, // Flag para identificar modificaciones reales
        };
      }
    );

    const allRemainingBeneficiaries = [
      ...beneficiariesWithRealModifications.map((b) => ({
        id: b.id,
        name: `${b.nombre} ${b.apellidoPaterno}`,
        percentage: b.porcentaje,
        wasManuallyModified: b.wasManuallyModified, // Mantener este flag
      })),
      ...addedBeneficiarios.map((b) => ({
        id: Number.parseInt(b.id),
        name: `${b.nombre} ${b.apellidoPaterno}`,
        percentage: b.porcentaje,
        wasManuallyModified: true, // Los agregados siempre son modificaciones
      })),
    ];

    const totalAfterDeletion = allRemainingBeneficiaries.reduce(
      (sum, b) => sum + b.percentage,
      0
    );

    if (allRemainingBeneficiaries.length > 0) {
      setSmartDeleteData({
        beneficiary: {
          id: id,
          rut: rut,
          numeroPoliza: numeroPoliza,
          name: name,
          percentage: currentPercentage,
          type: "api",
        },
        remainingBeneficiaries: allRemainingBeneficiaries,
        totalAfterDeletion: totalAfterDeletion,
      });
      setSmartDeleteDialogOpen(true);
    } else {
      performDeleteApiBeneficiary(id, rut, numeroPoliza);
    }
  };

  const performDeleteApiBeneficiary = (
    id: number,
    rut: number,
    numeroPoliza: number
  ) => {
    const poliza = polizas.find((p) => p.numeroPoliza === numeroPoliza);
    if (!poliza) return;

    const beneficiario = poliza.beneficiarios?.find((b) => b.id === id);
    if (!beneficiario) return;

    const modified = getModifiedApiBeneficiario(beneficiario.id);
    const currentBeneficiario = modified || beneficiario;
    const currentPercentage = currentBeneficiario.porcentaje;

    const apiBeneficiarios = poliza.beneficiarios || [];
    const addedBeneficiarios = beneficiariosByPoliza[numeroPoliza] || [];

    const remainingApiBeneficiarios = apiBeneficiarios.filter(
      (b) => b.id !== id && !deletedApiBeneficiaries.has(b.id)
    );

    const remainingWithModifications = remainingApiBeneficiarios.map((b) => {
      const modified = getModifiedApiBeneficiario(b.id);
      return modified || b;
    });

    const allRemainingBeneficiaries = [
      ...remainingWithModifications.map((b) => ({
        id: b.id,
        type: "api" as const,
        percentage: b.porcentaje,
      })),
      ...addedBeneficiarios.map((b) => ({
        id: b.id,
        type: "added" as const,
        percentage: b.porcentaje,
      })),
    ];
    console.log(currentPercentage);
    markApiBeneficiarioForDeletion({
      id: beneficiario.id,
      numeroPoliza: numeroPoliza,
      tipoBeneficiario: 4,
      rut: beneficiario.rut,
      dv: beneficiario.dv,
      nombre: beneficiario.nombre || "DATOS USUARIO",
      apellidoPaterno: beneficiario.apellidoPaterno || "DATOS USUARIO",
      apellidoMaterno: beneficiario.apellidoMaterno || "DATOS USUARIO",
      fechaNacimiento: beneficiario.fechaNacimiento || "DATOS USUARIO",
      parentesco:
        typeof beneficiario.parentesco === "string"
          ? 0
          : beneficiario.parentesco,
      email: beneficiario.email || "DATOS USUARIO",
      region: 1,
      ciudad: 56,
      comuna: 9,
      direccion: "USUARIO ELIMINADO",
      casaDepto: "USUARIO ELIMINADO",
      celular: beneficiario.celular || "USUARIO ELIMINADO",
      porcentaje: currentPercentage,

      sexo: beneficiario.sexo || 1,
    });

    if (allRemainingBeneficiaries.length > 0) {
      const currentTotal = allRemainingBeneficiaries.reduce(
        (sum, b) => sum + b.percentage,
        0
      );
      const deletedPercentage = currentPercentage;
      const totalAfterRedistribution = currentTotal + deletedPercentage;

      if (totalAfterRedistribution > 100) {
        const existingPercentages = allRemainingBeneficiaries.map(
          (b) => b.percentage
        );
        const normalizedPercentages = normalizePercentages(existingPercentages);

        allRemainingBeneficiaries.forEach((remaining, index) => {
          const normalizedPercentage = normalizedPercentages[index];

          if (remaining.type === "api") {
            const modifiedBeneficiario = getModifiedApiBeneficiario(
              remaining.id
            );
            const apiBeneficiario = poliza.beneficiarios?.find(
              (b) => b.id === remaining.id
            );
            if (apiBeneficiario) {
              const beneficiarioToUse = modifiedBeneficiario || apiBeneficiario;
              markApiBeneficiarioAsModified({
                id: apiBeneficiario.id,
                numeroPoliza: numeroPoliza,
                tipoBeneficiario: beneficiarioToUse.tipoBeneficiario || 4,
                rut: beneficiarioToUse.rut,
                dv: beneficiarioToUse.dv,
                nombre: beneficiarioToUse.nombre,
                apellidoPaterno: beneficiarioToUse.apellidoPaterno,
                apellidoMaterno: beneficiarioToUse.apellidoMaterno,
                fechaNacimiento: beneficiarioToUse.fechaNacimiento,
                parentesco:
                  typeof beneficiarioToUse.parentesco === "string"
                    ? 0
                    : beneficiarioToUse.parentesco,
                email: beneficiarioToUse.email || "",
                region: beneficiarioToUse.region || 0,
                ciudad: beneficiarioToUse.ciudad || 0,
                comuna: beneficiarioToUse.comuna || 0,
                direccion: beneficiarioToUse.direccion || "",
                casaDepto: beneficiarioToUse.casaDepto || "",
                celular: beneficiarioToUse.celular || "",
                porcentaje: normalizedPercentage ?? 0,
                sexo: beneficiarioToUse.sexo || 0,
              });
            }
          } else {
            const addedBeneficiario = addedBeneficiarios.find(
              (b) => b.id === remaining.id
            );
            if (addedBeneficiario) {
              updateBeneficiario(addedBeneficiario.id, {
                ...addedBeneficiario,
                porcentaje: normalizedPercentage,
              });
            }
          }
        });
      } else {
        const existingPercentages = allRemainingBeneficiaries.map(
          (b) => b.percentage
        );
        const redistributedPercentages = distributePercentageEqually(
          deletedPercentage,
          allRemainingBeneficiaries.length,
          existingPercentages
        );

        allRemainingBeneficiaries.forEach((remaining, index) => {
          const newPercentage = redistributedPercentages[index];

          if (remaining.type === "api") {
            const modifiedBeneficiario = getModifiedApiBeneficiario(
              remaining.id
            );
            const apiBeneficiario = poliza.beneficiarios?.find(
              (b) => b.id === remaining.id
            );
            if (apiBeneficiario) {
              const beneficiarioToUse = modifiedBeneficiario || apiBeneficiario;
              markApiBeneficiarioAsModified({
                id: apiBeneficiario.id,
                numeroPoliza: numeroPoliza,
                tipoBeneficiario: beneficiarioToUse.tipoBeneficiario || 4,
                rut: beneficiarioToUse.rut,
                dv: beneficiarioToUse.dv,
                nombre: beneficiarioToUse.nombre,
                apellidoPaterno: beneficiarioToUse.apellidoPaterno,
                apellidoMaterno: beneficiarioToUse.apellidoMaterno,
                fechaNacimiento: beneficiarioToUse.fechaNacimiento,
                parentesco:
                  typeof beneficiarioToUse.parentesco === "string"
                    ? 0
                    : beneficiarioToUse.parentesco,
                email: beneficiarioToUse.email || "",
                region: beneficiarioToUse.region || 0,
                ciudad: beneficiarioToUse.ciudad || 0,
                comuna: beneficiarioToUse.comuna || 0,
                direccion: beneficiarioToUse.direccion || "",
                casaDepto: beneficiarioToUse.casaDepto || "",
                celular: beneficiarioToUse.celular || "",
                porcentaje: newPercentage || 0,
                sexo: beneficiarioToUse.sexo || 0,
              });
            }
          } else {
            const addedBeneficiario = addedBeneficiarios.find(
              (b) => b.id === remaining.id
            );
            if (addedBeneficiario) {
              updateBeneficiario(addedBeneficiario.id, {
                ...addedBeneficiario,
                porcentaje: newPercentage,
              });
            }
          }
        });
      }
    }

    setDeletedApiBeneficiaries((prev) => new Set(prev).add(id));
    loadBeneficiarios();
    setHasChanges(hasAnyChanges());

    setTimeout(() => {
      if (asegurado) {
        loadPolizas(asegurado.rut);
      }
    }, 100);
  };

  const confirmDelete = () => {
    if (beneficiaryToDelete) {
      deleteBeneficiario(beneficiaryToDelete.id);
      loadBeneficiarios();
      setBeneficiaryToDelete(null);
      setHasChanges(hasAnyChanges());
    }
  };

  const confirmApiDelete = () => {
    if (apiBeneficiaryToDelete) {
      markApiBeneficiarioForDeletion({
        id: apiBeneficiaryToDelete.id,
        numeroPoliza: apiBeneficiaryToDelete.numeroPoliza,
        tipoBeneficiario: 4,
        rut: apiBeneficiaryToDelete.rut,
        dv: "",
        nombre: "",
        apellidoPaterno: "",
        apellidoMaterno: "",
        fechaNacimiento: "",
        parentesco: 0,
        email: "",
        region: 0,
        ciudad: 0,
        comuna: 0,
        direccion: "",
        casaDepto: "",
        celular: "",
        porcentaje: 0,
        sexo: 1,
      });
      setDeletedApiBeneficiaries((prev) =>
        new Set(prev).add(apiBeneficiaryToDelete.id)
      );
      setApiBeneficiaryToDelete(null);
      setHasChanges(hasAnyChanges());
    }
  };

  const handleConfirmSmartDelete = () => {
    if (smartDeleteData) {
      const { beneficiary } = smartDeleteData;

      if (beneficiary.type === "api") {
        performDeleteApiBeneficiary(
          beneficiary.id as number,
          beneficiary.rut,
          beneficiary.numeroPoliza
        );
      } else {
        performDeleteBeneficiary(
          beneficiary.id as string,
          beneficiary.numeroPoliza
        );
      }

      setSmartDeleteData(null);
      setSmartDeleteDialogOpen(false);
    }
  };

  const handleRestoreApiBeneficiary = (id: number) => {
    const poliza = polizas.find((p) =>
      p.beneficiarios?.some((b) => b.id === id)
    );
    if (!poliza) return;

    const beneficiario = poliza.beneficiarios?.find((b) => b.id === id);
    if (!beneficiario) return;

    const modified = getModifiedApiBeneficiario(beneficiario.id);
    const currentBeneficiario = modified || beneficiario;
    const currentPercentage = currentBeneficiario.porcentaje;

    const apiBeneficiarios = poliza.beneficiarios || [];
    const addedBeneficiarios = beneficiariosByPoliza[poliza.numeroPoliza] || [];

    const remainingApiBeneficiarios = apiBeneficiarios.filter(
      (b) => b.id !== id && !deletedApiBeneficiaries.has(b.id)
    );

    const remainingWithModifications = remainingApiBeneficiarios.map((b) => {
      const modified = getModifiedApiBeneficiario(b.id);
      return modified || b;
    });

    const allRemainingBeneficiaries = [
      ...remainingWithModifications.map((b) => ({
        id: b.id,
        type: "api" as const,
        percentage: b.porcentaje,
      })),
      ...addedBeneficiarios.map((b) => ({
        id: b.id,
        type: "added" as const,
        percentage: b.porcentaje,
      })),
    ];

    if (allRemainingBeneficiaries.length > 0) {
      const redistributedAmount = roundPercentage(
        currentPercentage / allRemainingBeneficiaries.length
      );

      allRemainingBeneficiaries.forEach((remaining) => {
        if (remaining.type === "api") {
          const apiBeneficiario = poliza.beneficiarios?.find(
            (b) => b.id === remaining.id
          );
          if (apiBeneficiario) {
            const modified = getModifiedApiBeneficiario(apiBeneficiario.id);
            const currentData = modified || apiBeneficiario;

            const newPercentage = roundPercentage(
              currentData.porcentaje - redistributedAmount
            );

            markApiBeneficiarioAsModified({
              id: apiBeneficiario.id,
              numeroPoliza: poliza.numeroPoliza,
              tipoBeneficiario: currentData.tipoBeneficiario || 4,
              rut: currentData.rut,
              dv: currentData.dv,
              nombre: currentData.nombre,
              apellidoPaterno: currentData.apellidoPaterno,
              apellidoMaterno: currentData.apellidoMaterno,
              fechaNacimiento: currentData.fechaNacimiento,
              parentesco:
                typeof currentData.parentesco === "string"
                  ? 0
                  : currentData.parentesco,
              email: currentData.email || "",
              region: currentData.region || 0,
              ciudad: currentData.ciudad || 0,
              comuna: currentData.comuna || 0,
              direccion: currentData.direccion || "",
              casaDepto: currentData.casaDepto || "",
              celular: currentData.celular || "",
              porcentaje: newPercentage,
              sexo: currentData.sexo || 0,
            });
          }
        } else {
          const addedBeneficiario = addedBeneficiarios.find(
            (b) => b.id === remaining.id
          );
          if (addedBeneficiario) {
            const newPercentage = roundPercentage(
              addedBeneficiario.porcentaje - redistributedAmount
            );
            updateBeneficiario(addedBeneficiario.id, {
              ...addedBeneficiario,
              porcentaje: newPercentage,
            });
          }
        }
      });
    }

    unmarkApiBeneficiarioFromDeletion(id);
    setDeletedApiBeneficiaries((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });

    loadBeneficiarios();
    setHasChanges(hasAnyChanges());

    setTimeout(() => {
      if (asegurado) {
        loadPolizas(asegurado.rut);
      }
    }, 100);
  };

  const handleViewHistory = () => {
    navigate(`${baseUrl}/historial`);
  };

  const handleGoToSummary = () => {
    const polizas = usePolizasCacheStore.getState().polizas;

    const todosErrores: ContactError[] = [];
    const polizasConProblemas: string[] = [];

    // Filtrar solo pólizas que tienen beneficiarios (del API, modificados o agregados)
    const polizasConBeneficiarios = polizas.filter((poliza) => {
      const beneficiariosActuales = getBeneficiariosActuales(
        poliza.numeroPoliza
      );
      return beneficiariosActuales.length > 0;
    });

    // Si no hay pólizas con beneficiarios, permitir ir al resumen
    if (polizasConBeneficiarios.length === 0) {
      navigate(`${baseUrl}/resumen`);
      return;
    }

    // Validar SOLO los beneficiarios actuales
    polizasConBeneficiarios.forEach((poliza) => {
      const polizaNumero = poliza.numeroPoliza;

      // Obtener beneficiarios actuales (combinando API + modificados + agregados - eliminados)
      const beneficiariosActuales = getBeneficiariosActuales(polizaNumero);

      // Validar solo estos beneficiarios
      const resultadoValidacion = checkMissingContactDataMultiple(
        beneficiariosActuales
      );

      if (resultadoValidacion.tieneErrores) {
        // Agregar información de póliza a cada error
        const erroresConPoliza = resultadoValidacion.errores.map((error) => ({
          ...error,
          numeroPoliza: polizaNumero,
        }));

        todosErrores.push(...erroresConPoliza);
      }
    });
    const erroresUnicos = eliminarErroresDuplicados(todosErrores);
    if (erroresUnicos.length > 0) {
      mostrarErroresDetallados(erroresUnicos, polizasConProblemas);
      return;
    }

    navigate(`${baseUrl}/resumen`);
  };

  const getBeneficiariosActuales = (polizaNumero: number) => {
    const storage = getAllBeneficiariosStorage();
    const poliza = usePolizasCacheStore
      .getState()
      .polizas.find((p) => p.numeroPoliza === polizaNumero);

    if (!poliza) return [];

    const apiBeneficiarios = poliza.beneficiarios || [];

    // 1. Filtrar beneficiarios eliminados
    const beneficiariosNoEliminados = apiBeneficiarios.filter(
      (apiBenef) =>
        !storage.deleted.some((deleted) => deleted.id === apiBenef.id)
    );

    // 2. Aplicar modificaciones
    const beneficiariosConModificaciones = beneficiariosNoEliminados.map(
      (apiBenef) => {
        const modificacion = storage.modified.find(
          (mod) => mod.id === apiBenef.id
        );
        return modificacion ? { ...apiBenef, ...modificacion } : apiBenef;
      }
    );

    // 3. Agregar nuevos beneficiarios
    const beneficiariosAgregados = storage.added.filter(
      (added) => added.numeroPoliza === polizaNumero
    );

    return [...beneficiariosConModificaciones, ...beneficiariosAgregados];
  };

  const eliminarErroresDuplicados = (
    errores: ContactError[]
  ): ContactError[] => {
    const seen = new Set();

    return errores.filter((error) => {
      // Crear una clave única con el ID y los campos faltantes
      const clave = `${error.beneficiarioId}-${error.camposFaltantes
        .sort()
        .join(",")}`;

      if (seen.has(clave)) {
        return false; // Es duplicado, lo filtramos
      }

      seen.add(clave);
      return true;
    });
  };

  const mostrarErroresDetallados = (
    errores: ContactError[],
    polizasConProblemas: string[]
  ) => {
    const groupedErrors = errores.reduce((acc, error) => {
      const key = error.beneficiarioId.toString();

      if (!acc[key]) {
        acc[key] = {
          rut: key,
          nombre: error.nombre,
          errors: [],
          beneficiarioId: error.beneficiarioId,
        };
      }

      // Agregar campos faltantes como errores
      if (error.camposFaltantes.length > 0) {
        acc[key].errors.push(
          `Campos requeridos: ${error.camposFaltantes.join(", ")}`
        );
      }

      return acc;
    }, {} as Record<string, any>);

    const errorGroups = Object.values(groupedErrors);

    setErrorList(errorGroups);
    setErrorMessage(`${errorGroups.length} beneficiarios con errores`);
    setShowErrorDialog(true);
  };

  const handleLogout = () => {
    navigate(`${baseUrl}/login`);
    setTimeout(() => {
      const { clearAuth } = useAuthStore.getState();
      const { clearCache } = usePolizasCacheStore.getState();
      const { clearBeneficiarios } = useBeneficiariosStore.getState();
      const { clearConsent } = useConsentStore.getState();

      clearAuth();
      clearCache();
      clearBeneficiarios();
      clearConsent();
      localStorage.setItem("beneficiarios", "");
    }, 100);
  };

  const hasBeneficiarios = Object.keys(beneficiariosByPoliza).length > 0;

  const getParentescoLabel = (parentesco: number | string) => {
    if (typeof parentesco === "string") return parentesco;

    const labels: Record<number, string> = {
      5: "Cónyuge",
      6: "Hijo/a",
      7: "Padre",
      8: "Madre",
      9: "Hermano/a",
      10: "Otro",
    };
    return labels[parentesco] || "N/A";
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar onLogoutClick={handleLogout} />
      <main className="flex-1 bg-gradient-to-br from-[var(--color-dove)] to-[var(--color-z-white)] px-4 py-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--color-z-blue)]">
              Mis Pólizas
            </h1>
            <p className="mt-2 text-sm md:text-base text-muted-foreground">
              Gestiona los beneficiarios de tus pólizas activas
            </p>

            <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-3">
              <Button
                onClick={handleViewHistory}
                size="lg"
                className="w-full sm:w-auto gap-2 bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)] text-white font-semibold shadow-sm transition-colors"
              >
                <History className="h-5 w-5" />
                Ver Historial
              </Button>
              {!hasUserSeenModal && (
                <div className="inline-block relative group">
                  <Button
                    onClick={() => setShowPolizaModal(true)}
                    size="lg"
                    className="w-full sm:w-auto gap-2 bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)] text-white font-semibold shadow-md transition-colors"
                  >
                    <PlusCircle className="h-5 w-5" />
                    Agregar Póliza
                  </Button>

                  {/* Popup/Tooltip que aparece en hover */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-3 py-6 bg-gray-800 text-white text-xs rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                    <div className="relative">
                      Si no visualizas la póliza que necesitas designar pincha
                      Aquí
                      {/* Flecha del tooltip */}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
              )}

              {mounted && hasChanges && (
                <Button
                  onClick={handleGoToSummary}
                  size="lg"
                  className="w-full sm:w-auto gap-2 bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)] text-white font-semibold shadow-sm transition-colors"
                >
                  <FileCheck className="h-5 w-5" />
                  Finalizar Solicitud
                </Button>
              )}
            </div>
          </div>

          {error && !loading && polizas.length === 0 && (
            <Alert className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="rounded-lg border bg-card p-8 text-center">
              <p className="text-muted-foreground">Cargando pólizas...</p>
            </div>
          ) : polizas.length > 0 ? (
            <>
              {/* Desktop View */}
              <div className="hidden lg:block space-y-6">
                {polizas
                  .sort((a, b) => {
                    const beneficiariosA =
                      (beneficiariosByPoliza[a.numeroPoliza] || []).length +
                      (a.beneficiarios || []).length;
                    const beneficiariosB =
                      (beneficiariosByPoliza[b.numeroPoliza] || []).length +
                      (b.beneficiarios || []).length;

                    return beneficiariosB - beneficiariosA;
                  })
                  .map((poliza) => {
                    const beneficiarios =
                      beneficiariosByPoliza[poliza.numeroPoliza] || [];
                    const existingBeneficiarios = poliza.beneficiarios || [];
                    const totalBeneficiarios =
                      beneficiarios.length + existingBeneficiarios.length;
                    const apiBeneficiarios = poliza.beneficiarios || [];
                    const totalPercentage = getTotalPercentageByPoliza(
                      poliza.numeroPoliza,
                      apiBeneficiarios
                    );
                    const actions = getActionsForPoliza(poliza.numeroPoliza);
                    const hasActions =
                      actions.added.length > 0 || actions.deleted.length > 0;
                    const canReplicate =
                      totalPercentage <= 100 &&
                      hasActions &&
                      polizas.length > 1 &&
                      (firstCompletedPoliza === poliza.numeroPoliza ||
                        actions.added.length > 0);

                    return (
                      <div
                        key={poliza.id}
                        className="overflow-hidden rounded-lg border bg-card shadow-sm"
                      >
                        <div className="border-b bg-[var(--color-light-blue)]/10 p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 grid grid-cols-3 gap-6">
                              <div>
                                <p className="text-sm font-medium text-muted-foreground mb-1">
                                  N° Póliza
                                </p>
                                <p className="text-xl font-bold text-[var(--color-z-blue)]">
                                  {poliza.numeroPoliza}
                                </p>
                              </div>
                              {poliza.nombrePlan && (
                                <div>
                                  <p className="text-sm font-medium text-muted-foreground mb-1">
                                    Plan
                                  </p>
                                  <p className="font-semibold text-[var(--color-z-blue)]">
                                    {poliza.nombrePlan}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    {poliza.codigoPlan}
                                  </p>
                                </div>
                              )}
                              {!poliza.nombrePlan &&
                                poliza.rutFormatedContratante && (
                                  <div>
                                    <p className="text-sm font-medium text-muted-foreground mb-1">
                                      Rut Empresa
                                    </p>
                                    <p className="font-semibold text-[var(--color-z-blue)]">
                                      {poliza.rutFormatedContratante}
                                    </p>
                                  </div>
                                )}

                              <div>
                                <p className="text-sm font-medium text-muted-foreground mb-1">
                                  Beneficiarios
                                </p>
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant={
                                      totalBeneficiarios > 0 ? "zblue" : "zblue"
                                    }
                                    className="text-xs"
                                  >
                                    {totalBeneficiarios} Total
                                  </Badge>

                                  <Badge variant="zblue" className="text-xs">
                                    Tienes un {totalPercentage}%
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <Button
                              onClick={() =>
                                handleAddBeneficiary(poliza.numeroPoliza)
                              }
                              size="sm"
                              className="gap-2 bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)]"
                            >
                              <Plus className="h-4 w-4" />
                              Agregar Beneficiario
                            </Button>
                          </div>

                          {canReplicate && (
                            <div
                              key={poliza.numeroPoliza}
                              className="mt-4 p-4 rounded-lg bg-[var(--color-z-blue)]/5 border border-[var(--color-z-blue)]/20"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Copy className="h-5 w-5 text-[var(--color-z-blue)]" />
                                  <div>
                                    <Label
                                      htmlFor={`replicate-${poliza.numeroPoliza}`}
                                      className="text-sm font-semibold text-[var(--color-z-blue)] cursor-pointer"
                                    >
                                      Replicar cambios a otras pólizas
                                    </Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      Aplicar las mismas modificaciones a las{" "}
                                      pólizas con un porcentaje menor al 100%
                                    </p>
                                  </div>
                                </div>
                                <Switch
                                  id={`replicate-${poliza.numeroPoliza}`}
                                  checked={
                                    !!replicationEnabled[poliza.numeroPoliza]
                                  }
                                  onCheckedChange={(checked) => {
                                    handleReplicationToggle(
                                      poliza.numeroPoliza,
                                      checked
                                    );
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          {/* Beneficiarios Section */}
                          {totalBeneficiarios > 0 && (
                            <div className="mt-6">
                              <div className="grid gap-6 md:grid-cols-2 ">
                                {existingBeneficiarios.length > 0 && (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2 pb-2 border-b border-[var(--color-z-blue)]/40">
                                      <CheckCircle className="h-5 w-5 text-[var(--color-sky-blue)]" />
                                      <h4 className="font-semibold text-[var(--color-z-blue)]">
                                        Beneficiarios Registrados
                                      </h4>
                                      <Badge
                                        variant="zblue"
                                        className="text-xs ml-auto "
                                      >
                                        {existingBeneficiarios.length}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 ">
                                      {existingBeneficiarios.map(
                                        (beneficiario) => {
                                          const modified =
                                            getModifiedApiBeneficiario(
                                              beneficiario.id
                                            );
                                          const displayData =
                                            modified || beneficiario;
                                          const isModified = !!modified;
                                          const isDeleted =
                                            deletedApiBeneficiaries.has(
                                              beneficiario.id
                                            );
                                          const contactError =
                                            checkMissingContactData(
                                              displayData
                                            );
                                          const hasMissingContactData =
                                            contactError.camposFaltantes
                                              .length > 0;

                                          return (
                                            <div
                                              key={`existing-${beneficiario.id}`}
                                              className={`rounded-lg border-2 p-4 ${
                                                isDeleted
                                                  ? "border-red-400/60 bg-red-50/50"
                                                  : isModified
                                                  ? "border-amber-500/60 bg-amber-50/50"
                                                  : " text-amber-700 border-[var(--color-z-blue)]/40"
                                              }`}
                                            >
                                              <div className="flex items-start justify-between mb-2">
                                                <div className="flex-1">
                                                  <div className="flex items-center gap-2">
                                                    <p
                                                      className={`font-semibold text-[var(--color-z-blue)] ${
                                                        isDeleted
                                                          ? "line-through opacity-60"
                                                          : ""
                                                      }`}
                                                    >
                                                      {displayData.nombre}{" "}
                                                      {
                                                        displayData.apellidoPaterno
                                                      }{" "}
                                                      {
                                                        displayData.apellidoMaterno
                                                      }
                                                    </p>
                                                    {isDeleted ? (
                                                      <Badge
                                                        variant="outline"
                                                        className="text-xs bg-red-100 text-red-700 border-red-300"
                                                      >
                                                        Pendiente de eliminación
                                                      </Badge>
                                                    ) : isModified ? (
                                                      <Badge
                                                        variant="outline"
                                                        className="text-xs bg-amber-100 text-amber-700 border-amber-300"
                                                      >
                                                        Modificado
                                                      </Badge>
                                                    ) : null}

                                                    {!isDeleted &&
                                                      hasMissingContactData && (
                                                        <Badge
                                                          variant="outline"
                                                          className="text-xs bg-red-100 text-red-700 border-red-300"
                                                        >
                                                          Actualiza datos de
                                                          contactibilidad
                                                        </Badge>
                                                      )}
                                                  </div>
                                                  <div className="flex items-center gap-2 mt-1">
                                                    <Badge
                                                      variant="zblue"
                                                      className="text-xs"
                                                    >
                                                      {typeof displayData.parentesco ===
                                                      "string"
                                                        ? displayData.parentesco
                                                        : getParentescoLabel(
                                                            displayData.parentesco
                                                          )}
                                                    </Badge>
                                                    <Badge
                                                      variant="zblue"
                                                      className="text-xs  flex items-center gap-1"
                                                    >
                                                      <Percent className="h-3 w-3" />
                                                      {displayData.porcentaje}%
                                                    </Badge>
                                                  </div>
                                                </div>
                                                <div className="flex gap-1">
                                                  {isDeleted ? (
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      onClick={() =>
                                                        handleRestoreApiBeneficiary(
                                                          beneficiario.id
                                                        )
                                                      }
                                                      className="h-8 px-3 text-green-600 hover:bg-green-50 hover:text-green-700 gap-1"
                                                      title="Deshacer eliminación"
                                                    >
                                                      <Undo2 className="h-4 w-4" />
                                                      <span className="text-xs">
                                                        Deshacer
                                                      </span>
                                                    </Button>
                                                  ) : (
                                                    <>
                                                      <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() =>
                                                          handleEditApiBeneficiary(
                                                            poliza.numeroPoliza,
                                                            beneficiario
                                                          )
                                                        }
                                                        className="h-8 w-8 p-0 text-[var(--color-z-blue)] hover:bg-[var(--color-z-blue)]/10"
                                                        title="Editar beneficiario"
                                                      >
                                                        <Edit className="h-4 w-4" />
                                                      </Button>
                                                      <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() =>
                                                          handleDeleteApiBeneficiary(
                                                            beneficiario.id,
                                                            beneficiario.rut,
                                                            poliza.numeroPoliza,
                                                            `${beneficiario.nombre} ${beneficiario.apellidoPaterno}`
                                                          )
                                                        }
                                                        className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                                                        title="Eliminar beneficiario"
                                                      >
                                                        <Trash2 className="h-4 w-4" />
                                                      </Button>
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="grid grid-cols-2 gap-2 text-sm mt-2 ">
                                                <div>
                                                  <span className="text-muted-foreground">
                                                    RUT:
                                                  </span>{" "}
                                                  <span className="font-medium text-[var(--color-z-blue)]">
                                                    {formatRutFromParts(
                                                      displayData.rut,
                                                      displayData.dv
                                                    )}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-muted-foreground">
                                                    F. Nacimiento:
                                                  </span>{" "}
                                                  <span className="font-medium text-[var(--color-z-blue)]">
                                                    {new Date(
                                                      displayData.fechaNacimiento
                                                    ).toLocaleDateString(
                                                      "es-CL"
                                                    )}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  </div>
                                )}

                                {beneficiarios.length > 0 && (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2 pb-2 border-b border-[var(--color-z-blue)]/30">
                                      <UserPlus className="h-5 w-5 text-[var(--color-z-blue)]" />
                                      <h4 className="font-semibold text-[var(--color-z-blue)]">
                                        Beneficiarios Agregados
                                      </h4>
                                      <Badge
                                        variant="zblue"
                                        className="text-xs ml-auto"
                                      >
                                        {beneficiarios.length}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2">
                                      {beneficiarios.map((beneficiario) => {
                                        const isAdded =
                                          beneficiario.id.includes(
                                            "replicated"
                                          ) ||
                                          beneficiario.id.includes("temp") ||
                                          beneficiario.id.includes("added");

                                        const isModified =
                                          isAdded &&
                                          beneficiario.hasBeenModified;
                                        return (
                                          <div
                                            key={beneficiario.id}
                                            className={`rounded-lg border-2 p-4 ${
                                              isModified
                                                ? "border-amber-500/60 bg-amber-50/50"
                                                : " text-amber-700 border-[var(--color-z-blue)]/40"
                                            }`}
                                          >
                                            <div className="flex items-start justify-between mb-2">
                                              <div className="flex-1">
                                                <div className="flex flex-items gap-2">
                                                  <p className="font-semibold text-[var(--color-z-blue)]">
                                                    {beneficiario.nombre}{" "}
                                                    {
                                                      beneficiario.apellidoPaterno
                                                    }{" "}
                                                    {
                                                      beneficiario.apellidoMaterno
                                                    }
                                                  </p>
                                                  {isModified ? (
                                                    <Badge
                                                      variant="outline"
                                                      className="text-xs bg-amber-100 text-amber-700 border-amber-300 "
                                                    >
                                                      Modificado
                                                    </Badge>
                                                  ) : null}
                                                </div>

                                                <div className="flex items-center gap-2 mt-1">
                                                  <Badge
                                                    variant="zblue"
                                                    className="text-xs"
                                                  >
                                                    {beneficiario.tipoBeneficiario ==
                                                    "4"
                                                      ? "Beneficiario"
                                                      : beneficiario.tipoBeneficiario ===
                                                        "3"
                                                      ? "Beneficiario contingente"
                                                      : "Beneficiario"}
                                                  </Badge>
                                                  <Badge
                                                    variant="zblue"
                                                    className="text-xs flex items-center gap-1"
                                                  >
                                                    <Percent className="h-3 w-3" />
                                                    {beneficiario.porcentaje}%
                                                  </Badge>
                                                </div>
                                              </div>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  handleEditApiBeneficiary(
                                                    poliza.numeroPoliza,
                                                    beneficiario
                                                  )
                                                }
                                                className="h-8 w-8 p-0 text-[var(--color-z-blue)] hover:bg-[var(--color-z-blue)]/10"
                                                title="Editar beneficiario"
                                              >
                                                <Edit className="h-4 w-4" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  handleDeleteBeneficiary(
                                                    beneficiario.id,
                                                    `${beneficiario.nombre} ${beneficiario.apellidoPaterno}`
                                                  )
                                                }
                                                className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                                              <div>
                                                <span className="text-muted-foreground">
                                                  RUT:
                                                </span>{" "}
                                                <span className="font-medium text-[var(--color-z-blue)]">
                                                  {formatRutFromParts(
                                                    beneficiario.rut,
                                                    beneficiario.dv
                                                  )}
                                                </span>
                                              </div>
                                              <div>
                                                <span className="text-muted-foreground">
                                                  Parentesco:
                                                </span>{" "}
                                                <span className="font-medium text-[var(--color-z-blue)]">
                                                  {getParentescoLabel(
                                                    beneficiario.parentesco
                                                  )}
                                                </span>
                                              </div>
                                              <div className="col-span-2">
                                                <span className="text-muted-foreground">
                                                  Email:
                                                </span>{" "}
                                                <span className="font-medium text-[var(--color-z-blue)]">
                                                  {beneficiario.email}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {totalBeneficiarios === 0 && (
                            <div className="mt-6 text-center">
                              <p className="text-sm text-muted-foreground">
                                No hay beneficiarios registrados. Haz clic en
                                "Agregar" para comenzar.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div className="space-y-4 lg:hidden">
                {polizas.map((poliza) => {
                  const beneficiarios =
                    beneficiariosByPoliza[poliza.numeroPoliza] || [];
                  const existingBeneficiarios = poliza.beneficiarios || [];
                  const totalBeneficiarios =
                    beneficiarios.length + existingBeneficiarios.length;
                  const apiBeneficiarios = poliza.beneficiarios || [];
                  const totalPercentage = getTotalPercentageByPoliza(
                    poliza.numeroPoliza,
                    apiBeneficiarios
                  );

                  const actions = getActionsForPoliza(poliza.numeroPoliza);
                  const hasActions =
                    actions.added.length > 0 ||
                    actions.modified.length > 0 ||
                    actions.deleted.length > 0;
                  const canReplicate =
                    totalPercentage <= 100 &&
                    hasActions &&
                    polizas.length > 1 &&
                    (firstCompletedPoliza === poliza.numeroPoliza ||
                      actions.added.length > 0);

                  return (
                    <div
                      key={poliza.id}
                      className="rounded-lg border bg-card shadow-sm overflow-hidden"
                    >
                      <div className="border-b bg-[var(--color-light-blue)]/10 p-4">
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">
                                {`${canReplicate}`}
                                Póliza N°
                              </p>
                              <p className="text-xl font-bold text-[var(--color-z-blue)]">
                                {poliza.numeroPoliza}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge
                                variant={
                                  totalBeneficiarios > 0 ? "zblue" : "zblue"
                                }
                                className="text-xs"
                              >
                                {totalBeneficiarios} Benef.
                              </Badge>
                              <Badge variant={"zblue"} className="text-xs">
                                {totalPercentage}%
                              </Badge>
                            </div>
                          </div>

                          {poliza.nombrePlan && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">
                                Plan
                              </p>
                              <p className="text-sm font-semibold text-[var(--color-z-blue)]">
                                {poliza.nombrePlan}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {poliza.codigoPlan}
                              </p>
                            </div>
                          )}

                          <Button
                            onClick={() =>
                              handleAddBeneficiary(poliza.numeroPoliza)
                            }
                            className="w-full gap-2 bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)]"
                          >
                            <Plus className="h-4 w-4" />
                            Agregar Beneficiario
                          </Button>

                          {canReplicate && (
                            <div className="p-3 rounded-lg bg-[var(--color-z-blue)]/5 border border-[var(--color-z-blue)]/20">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-start gap-2 flex-1">
                                  <Copy className="h-4 w-4 text-[var(--color-z-blue)] mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <Label
                                      htmlFor={`replicate-mobile-${poliza.numeroPoliza}`}
                                      className="text-xs font-semibold text-[var(--color-z-blue)] cursor-pointer block"
                                    >
                                      Replicar cambios
                                    </Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      A {polizas.length - 1} pólizas
                                    </p>
                                  </div>
                                </div>
                                <Switch
                                  id={`replicate-mobile-${poliza.numeroPoliza}`}
                                  checked={
                                    replicationEnabled[poliza.numeroPoliza] ||
                                    false
                                  }
                                  onCheckedChange={(checked) =>
                                    handleReplicationToggle(
                                      poliza.numeroPoliza,
                                      checked
                                    )
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mobile Beneficiaries Display */}
                        {totalBeneficiarios > 0 && (
                          <div className="mt-4 space-y-3">
                            {/* Existing Beneficiaries */}
                            {existingBeneficiarios.length > 0 && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 pb-2 border-b border-[var(--color-sky-blue)]/30">
                                  <CheckCircle className="h-4 w-4 text-[var(--color-sky-blue)]" />
                                  <h4 className="text-sm font-semibold text-[var(--color-z-blue)]">
                                    Registrados
                                  </h4>
                                  <Badge
                                    variant="zblue"
                                    className="text-xs ml-auto"
                                  >
                                    {existingBeneficiarios.length}
                                  </Badge>
                                </div>
                                {existingBeneficiarios.map((beneficiario) => {
                                  const modified = getModifiedApiBeneficiario(
                                    beneficiario.id
                                  );

                                  const displayData = modified || beneficiario;

                                  const isDeleted = deletedApiBeneficiaries.has(
                                    beneficiario.id
                                  );

                                  const isModified =
                                    modified &&
                                    !isDeleted &&
                                    (modified.porcentaje !==
                                      beneficiario.porcentaje ||
                                      modified.nombre !== beneficiario.nombre ||
                                      modified.apellidoPaterno !==
                                        beneficiario.apellidoPaterno);

                                  return (
                                    <div
                                      key={`existing-mobile-${beneficiario.id}`}
                                      className={`rounded-lg border-2 p-3 ${
                                        isDeleted
                                          ? "border-red-400/60 bg-red-50/50"
                                          : isModified
                                          ? "border-amber-500/60 bg-amber-50/50"
                                          : "border-[var(--color-sky-blue)]/40 bg-[var(--color-sky-blue)]/5"
                                      }`}
                                    >
                                      <div className="space-y-2">
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex-1 min-w-0">
                                            <p
                                              className={`text-sm font-semibold text-[var(--color-z-blue)] ${
                                                isDeleted
                                                  ? "line-through opacity-60"
                                                  : ""
                                              }`}
                                            >
                                              {displayData.nombre}{" "}
                                              {displayData.apellidoPaterno}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                              {formatRutFromParts(
                                                beneficiario.rut,
                                                beneficiario.dv
                                              )}
                                            </p>
                                          </div>
                                          {isDeleted ? (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() =>
                                                handleRestoreApiBeneficiary(
                                                  beneficiario.id
                                                )
                                              }
                                              className="h-7 px-2 text-green-600 hover:bg-green-50 hover:text-green-700 flex-shrink-0"
                                            >
                                              <Undo2 className="h-3 w-3 mr-1" />
                                              <span className="text-xs">
                                                Deshacer
                                              </span>
                                            </Button>
                                          ) : (
                                            <div className="flex gap-1 flex-shrink-0">
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  handleEditApiBeneficiary(
                                                    poliza.numeroPoliza,
                                                    beneficiario
                                                  )
                                                }
                                                className="h-7 w-7 p-0 text-[var(--color-z-blue)] hover:bg-[var(--color-z-blue)]/10"
                                              >
                                                <Edit className="h-3 w-3" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  handleDeleteApiBeneficiary(
                                                    beneficiario.id,
                                                    beneficiario.rut,
                                                    poliza.numeroPoliza,
                                                    `${beneficiario.nombre} ${beneficiario.apellidoPaterno}`
                                                  )
                                                }
                                                className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1.5">
                                          {isDeleted ? (
                                            <Badge
                                              variant="outline"
                                              className="text-xs bg-red-100 text-red-700 border-red-300"
                                            >
                                              Pendiente eliminación
                                            </Badge>
                                          ) : isModified ? (
                                            <Badge
                                              variant="outline"
                                              className="text-xs bg-amber-100 text-amber-700 border-amber-300"
                                            >
                                              Modificado
                                            </Badge>
                                          ) : null}
                                          <Badge
                                            variant="zblue"
                                            className="text-xs"
                                          >
                                            {typeof displayData.parentesco ===
                                            "string"
                                              ? displayData.parentesco
                                              : getParentescoLabel(
                                                  displayData.parentesco
                                                )}
                                          </Badge>
                                          <Badge
                                            variant="zblue"
                                            className="text-xs flex items-center gap-1"
                                          >
                                            <Percent className="h-3 w-3" />
                                            {displayData.porcentaje}%
                                          </Badge>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Added Beneficiaries */}
                            {beneficiarios.length > 0 && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 pb-2 border-b border-[var(--color-z-blue)]/30">
                                  <UserPlus className="h-4 w-4 text-[var(--color-z-blue)]" />
                                  <h4 className="text-sm font-semibold text-[var(--color-z-blue)]">
                                    Agregados
                                  </h4>
                                  <Badge
                                    variant="zblue"
                                    className="text-xs ml-auto"
                                  >
                                    {beneficiarios.length}
                                  </Badge>
                                </div>
                                {beneficiarios.map((beneficiario) => (
                                  <div
                                    key={`added-mobile-${beneficiario.id}`}
                                    className="rounded-lg border-2 border-[var(--color-z-blue)]/30 bg-white p-3"
                                  >
                                    <div className="space-y-2">
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-semibold text-[var(--color-z-blue)]">
                                            {beneficiario.nombre}{" "}
                                            {beneficiario.apellidoPaterno}
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            {formatRutFromParts(
                                              beneficiario.rut,
                                              beneficiario.dv
                                            )}
                                          </p>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            handleDeleteBeneficiary(
                                              beneficiario.id,
                                              `${beneficiario.nombre} ${beneficiario.apellidoPaterno}`
                                            )
                                          }
                                          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 flex-shrink-0"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <Badge
                                          variant="zblue"
                                          className="text-xs"
                                        >
                                          {beneficiario.tipoBeneficiario}
                                        </Badge>
                                        <Badge
                                          variant="zblue"
                                          className="text-xs"
                                        >
                                          {getParentescoLabel(
                                            beneficiario.parentesco
                                          )}
                                        </Badge>
                                        <Badge
                                          variant="zblue"
                                          className="text-xs flex items-center gap-1"
                                        >
                                          <Percent className="h-3 w-3" />
                                          {beneficiario.porcentaje}%
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {totalBeneficiarios === 0 && (
                          <div className="mt-4 text-center py-4">
                            <p className="text-xs text-muted-foreground">
                              No hay beneficiarios. Toca "Agregar" para
                              comenzar.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      </main>
      <Footer />

      {selectedPoliza && (
        <BeneficiarioModalForm
          open={modalOpen}
          onOpenChange={setModalOpen}
          numeroPoliza={selectedPoliza}
          onSuccess={handleBeneficiaryAdded}
          editMode={editMode}
          existingBeneficiario={beneficiaryToEdit || undefined}
          apiBeneficiarios={
            polizas.find((p) => p.numeroPoliza === selectedPoliza)
              ?.beneficiarios || []
          }
          rutContratante={getRutContratanteFormateado(selectedPoliza)}
        />
      )}

      {showErrorDialog && (
        <ErrorDialog
          open={showErrorDialog}
          onOpenChange={setShowErrorDialog}
          errorList={errorList}
          errorMessage={errorMessage}
          expandedItems={expandedItems}
          onToggleItem={toggleItem}
        />
      )}
      {showPolizaModal && (
        <PolizasForm open={showPolizaModal} onOpenChange={setShowPolizaModal} />
      )}

      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={confirmDelete}
          beneficiaryName={beneficiaryToDelete?.name || ""}
        />
      )}

      {apiDeleteDialogOpen && (
        <DeleteConfirmationDialog
          open={apiDeleteDialogOpen}
          onOpenChange={setApiDeleteDialogOpen}
          onConfirm={confirmApiDelete}
          beneficiaryName={apiBeneficiaryToDelete?.name || ""}
        />
      )}

      {smartDeleteDialogOpen && smartDeleteData && (
        <SmartDeleteDialog
          open={smartDeleteDialogOpen}
          onOpenChange={setSmartDeleteDialogOpen}
          onConfirm={handleConfirmSmartDelete}
          beneficiaryName={smartDeleteData.beneficiary.name}
          beneficiaryPercentage={smartDeleteData.beneficiary.percentage}
          remainingBeneficiaries={smartDeleteData.remainingBeneficiaries}
          totalAfterDeletion={smartDeleteData.totalAfterDeletion}
        />
      )}
    </div>
  );
}
